/**
 * Utility functions for error handling
 */

/**
 * Safely extracts HTTP status code from an error object.
 *
 * Commonly used with RTK Query errors which contain a 'status' property.
 * This utility handles the type checking and conversion safely.
 *
 * @param error - Unknown error object, typically from RTK Query
 * @returns The numeric status code if available, undefined otherwise
 *
 * @example
 * ```tsx
 * const { data, isLoading, isError, error } = useGetTopicQuery(topicId);
 *
 * if (isError) {
 *   return <ErrorView statusCode={getStatusCodeFromError(error)} />;
 * }
 * ```
 */
export const getStatusCodeFromError = (error: unknown): number | undefined => {
  if (error && typeof error === 'object' && 'status' in error) {
    const statusValue = (error as { status: unknown }).status;

    // Return if already a valid number
    if (typeof statusValue === 'number' && !Number.isNaN(statusValue)) {
      return statusValue;
    }

    // Try to convert string numbers (e.g., "404" -> 404)
    if (typeof statusValue === 'string') {
      const parsed = Number.parseInt(statusValue, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
  }

  return undefined;
};
